// Motion Director plan → editor box specs. Pure port of the reference importer
// (motion_director_v1/aidea_motion_importer_v1.js: svgMarkup + textHtml); the
// editor-handle hunt, the webpack-chunk hack and the 110ms waits are gone —
// applyMotionPlan.js dispatches the real actions. The plan is validated
// server-side (aidea-middleware motion-plan.validate.ts); colours and fonts are
// re-checked here anyway because they land in style attributes.

import {
  getBoxAnimationPresets,
  getBoxExitAnimationPresets,
  normalizeAnimationName,
} from "./animationHelpers";
import { FPS } from "../../common/sceneTimeline.es6";
import { ID_PREFIX_BOX } from "../../common/constants";
import {
  generateGoogleFontsLink,
  isSafeFontName,
  isSupportedGoogleFont,
} from "../../common/font_extractor.es6";

export const CANVAS_W = 1920;
export const CANVAS_H = 1080;
// The canvas root font-size is 14px per 1100px of canvas width (changeFontBase,
// common_tools.es6), so 1em = 24.436 plan px at ANY canvas size — measured live
// 2026-10-04 (rendered px × 1920 / canvas width ÷ em = 24.437).
export const PX_PER_EM = (14 * CANVAS_W) / 1100;
const SAFE_LEFT = 110;
const SAFE_RIGHT = 1810;
const MIN_SIZE_SCALE = 0.7; // never shrink text below 70% of the planned size
const FIT_SLACK = 1.03; // measured width × slack, so sub-pixel rounding never clips
// Every box this module creates carries "-mdir-" in its id: a re-run deletes the
// previous run's boxes by this tag (applyMotionPlan.js), nothing else.
/**
 * The only URLs a plan may put into a box (icon srcs and swapped images): https, or our own
 * middleware origin (local gated mode serves http://localhost). Anything else (data:, javascript:, other http) is dropped.
 */
export function isAllowedAssetUrl(url) {
  if (typeof url !== "string") return false;
  if (/^https:\/\//i.test(url)) return true;
  const mw = process.env.MW_API_URL;
  return !!mw && url.startsWith(mw);
}

export const MDIR_ID_TAG = "mdir";

const COLOR_RE = /^#[0-9a-fA-F]{3,8}$|^rgba?\([\d.,\s]+\)$/;
const SERIF_STACK = { Zodiak: "Zodiak,Georgia,'Times New Roman',serif" };
const ICONS = {
  headset:
    '<path d="M4 14v-2a8 8 0 0 1 16 0v2"/><path d="M4 14h3v5H5a1 1 0 0 1-1-1z"/><path d="M20 14h-3v5h2a1 1 0 0 0 1-1z"/><path d="M18 19c0 1.6-2 2.4-5 2.4"/>',
  chat: '<path d="M21 11.5a8.5 8.5 0 0 1-12.3 7.6L3.5 20.5l1.4-4.8A8.5 8.5 0 1 1 21 11.5z"/><path d="M8.5 11.5h.01M12.5 11.5h.01M16.5 11.5h.01"/>',
  shield:
    '<path d="M12 3l7.5 3v6c0 4.6-3.2 7.8-7.5 9.3C7.7 19.8 4.5 16.6 4.5 12V6z"/><path d="M8.8 12.2l2.3 2.3 4.3-4.6"/>',
  heart:
    '<path d="M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20z"/>',
  doc: '<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4"/><path d="M10 12h5M10 16h5"/>',
  car: '<path d="M3.5 16v-3.5L6 7.5h12l2.5 5V16z"/><path d="M3.5 12.5h17"/><circle cx="7.5" cy="16.8" r="1.7"/><circle cx="16.5" cy="16.8" r="1.7"/>',
  globe:
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3c3 3.4 3 14.6 0 18M12 3c-3 3.4-3 14.6 0 18"/>',
  gear: '<circle cx="12" cy="12" r="3.2"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1"/><circle cx="12" cy="12" r="6.5"/>',
  spark:
    '<path d="M12 3c.8 4.6 2.4 6.2 7 7-4.6.8-6.2 2.4-7 7-.8-4.6-2.4-6.2-7-7 4.6-.8 6.2-2.4 7-7z"/><path d="M19 3.5v3M17.5 5h3"/>',
  star: '<path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.8l-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z"/>',
  users:
    '<circle cx="9" cy="8" r="3.2"/><path d="M3 20c0-3.4 2.7-6 6-6s6 2.6 6 6"/><circle cx="17" cy="9" r="2.5"/><path d="M16 14.2c2.9.1 5 2.4 5 5.3"/>',
  check: '<circle cx="12" cy="12" r="9"/><path d="M8 12.3l2.7 2.7L16 9.6"/>',
  phone:
    '<path d="M5 4h4l1.5 4.5-2.3 1.4a11 11 0 0 0 6 6l1.4-2.3L20 15v4a1.5 1.5 0 0 1-1.6 1.5C10.6 20 4 13.4 3.5 5.6A1.5 1.5 0 0 1 5 4z"/>',
  mail: '<rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M3.5 7l8.5 6 8.5-6"/>',
  chart: '<path d="M4 20V4"/><path d="M4 20h16"/><path d="M8 16v-4M12 16V8M16 16v-6"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  bulb: '<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z"/>',
  target:
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.2"/>',
};
const NS = 'xmlns="http://www.w3.org/2000/svg"';

const escText = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escAttr = (s) => escText(s).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
const safeColor = (c, fallback) => (typeof c === "string" && COLOR_RE.test(c) ? c : fallback);
const num = (n, fallback = 0) => (Number.isFinite(Number(n)) ? Number(n) : fallback);
const pct = (v, base) => +((num(v) / base) * 100).toFixed(3);
const uri = (s) => "data:image/svg+xml;charset=utf-8," + encodeURIComponent(s);

/* ---------- SVG primitives → markup (reference importer, attribute-escaped) ---------- */
export function svgMarkup(el) {
  const { kind, params: p = {} } = el.svg;
  const w = num(el.w);
  const h = num(el.h);
  const W = CANVAS_W;
  const H = CANVAS_H;
  const col = (c, fallback) => escAttr(safeColor(c, fallback));
  switch (kind) {
    case "scrim": {
      const cx = num(p.cx);
      const cy = num(p.cy);
      const o = num(p.opacity, 0.85);
      const c = col(p.color, "#05070F");
      return `<svg ${NS} viewBox="0 0 ${W} ${H}" preserveAspectRatio="none"><defs><radialGradient id="g" cx="${cx}" cy="${cy}" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${cx} ${cy}) scale(${num(p.rx, 1)} ${num(p.ry, 1)}) translate(${-cx} ${-cy})"><stop offset="0" stop-color="${c}" stop-opacity="${o}"/><stop offset=".55" stop-color="${c}" stop-opacity="${o * 0.55}"/><stop offset="1" stop-color="${c}" stop-opacity="0"/></radialGradient></defs><rect width="${W}" height="${H}" fill="url(#g)"/></svg>`;
    }
    case "veil":
      return `<svg ${NS} viewBox="0 0 16 9" preserveAspectRatio="none"><rect width="16" height="9" fill="${col(p.color, "#05070F")}" fill-opacity="${num(p.opacity, 0.7)}"/></svg>`;
    case "line": {
      const t = num(p.thickness, h) || 2;
      return `<svg ${NS} viewBox="0 0 ${w} ${t}"><rect width="${w}" height="${t}" rx="${t / 2}" fill="${col(p.color, "#FFFFFF")}"/></svg>`;
    }
    case "rect":
      return `<svg ${NS} viewBox="0 0 ${w} ${h}"><rect width="${w}" height="${h}" rx="${num(p.radius)}" fill="${col(p.color, "#FFFFFF")}"/></svg>`;
    case "arrow": {
      const from = col(p.from_color, "#FFFFFF");
      const to = col(p.to_color, "#FFFFFF");
      return `<svg ${NS} viewBox="0 0 ${w} ${h}"><defs><linearGradient id="a" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${w}" y2="0"><stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs><path d="M4 ${h / 2}H${w - 20}" stroke="url(#a)" stroke-width="4" ${p.dashed ? 'stroke-dasharray="3 10"' : ""} stroke-linecap="round"/><path d="M${w - 28} ${h / 2 - 10}l12 10-12 10" fill="none" stroke="${to}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    }
    case "bridge": {
      const from = col(p.from_color, "#FFFFFF");
      const to = col(p.to_color, "#FFFFFF");
      return `<svg ${NS} viewBox="0 0 ${w} ${h}"><defs><linearGradient id="b" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${w}" y2="0"><stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs><circle cx="8" cy="${h / 2}" r="6" fill="${from}"/><path d="M18 ${h / 2} C ${w * 0.29} ${h / 2 - 28}, ${w * 0.71} ${h / 2 + 28}, ${w - 18} ${h / 2}" fill="none" stroke="url(#b)" stroke-width="3" stroke-dasharray="3 10" stroke-linecap="round"/><circle cx="${w - 8}" cy="${h / 2}" r="6" fill="${to}"/></svg>`;
    }
    case "icon_chip": {
      const c = col(p.color, "#FFFFFF");
      const s = w;
      const r = s / 2 - 2;
      const k = s / 48;
      const ring =
        p.style === "glass"
          ? `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".34"/><stop offset="1" stop-color="#fff" stop-opacity=".08"/></linearGradient></defs><circle cx="${s / 2}" cy="${s / 2}" r="${r}" fill="url(#g)" stroke="#fff" stroke-opacity=".55" stroke-width="1.5"/>`
          : p.style === "solid"
            ? `<circle cx="${s / 2}" cy="${s / 2}" r="${r}" fill="${col(p.fill, safeColor(p.color, "#FFFFFF"))}"/>`
            : `<circle cx="${s / 2}" cy="${s / 2}" r="${r}" fill="${col(p.fill, "rgba(8,10,20,.55)")}" stroke="${c}" stroke-opacity=".85" stroke-width="2"/>`;
      return `<svg ${NS} viewBox="0 0 ${s} ${s}">${ring}<g transform="translate(${s / 4} ${s / 4}) scale(${k})" fill="none" stroke="${p.style === "solid" ? "#fff" : c}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${ICONS[p.icon] || ICONS.star}</g></svg>`;
    }
    case "node":
      return `<svg ${NS} viewBox="0 0 96 96"><defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="6" stdDeviation="6" flood-color="#05194F" flood-opacity=".3"/></filter></defs><circle cx="48" cy="46" r="40" fill="#fff" filter="url(#s)"/><g transform="translate(28 26) scale(1.67)" fill="none" stroke="${col(p.color, "#183690")}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICONS[p.icon] || ICONS.star}</g></svg>`;
    case "dashed_path": {
      const P = (Array.isArray(p.points) ? p.points : []).map((pt) => [num(pt[0]), num(pt[1])]);
      if (P.length < 2) return `<svg ${NS} viewBox="0 0 1 1"/>`;
      let d = `M${P[0][0]} ${P[0][1]}`;
      for (let i = 1; i < P.length; i++) {
        const a = P[i - 1];
        const b = P[i];
        const mx = (a[0] + b[0]) / 2;
        d += ` C ${mx} ${a[1]}, ${mx} ${b[1]}, ${b[0]} ${b[1]}`;
      }
      return `<svg ${NS} viewBox="${num(el.x)} ${num(el.y)} ${w} ${h}"><path d="${d}" fill="none" stroke="${col(p.color, "#FFFFFF")}" stroke-width="3" stroke-linecap="round" stroke-dasharray="2 12"/></svg>`;
    }
    case "wave_band": {
      const grad = String(p.fill).startsWith("gradient:")
        ? String(p.fill).slice(9).split(",")
        : null;
      const fill = grad ? "url(#s)" : col(p.fill, "#FFFFFF");
      const corner = p.corner_rings
        ? '<g fill="none" stroke="#fff" stroke-opacity=".22" stroke-width="2">' +
          [260, 360, 460].map((r) => `<circle cx="1780" cy="980" r="${r}"/>`).join("") +
          "</g>"
        : "";
      const defs = grad
        ? `<defs><linearGradient id="s" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${col(grad[0], "#FFFFFF")}"/><stop offset="1" stop-color="${col(grad[1], "#FFFFFF")}"/></linearGradient></defs>`
        : "";
      return `<svg ${NS} viewBox="0 0 ${W} ${H}">${defs}<g transform="translate(${num(p.dx)} ${num(p.dy)})" opacity="${num(p.opacity, 1)}"><path d="${escAttr(p.path || "")}" fill="${fill}"/>${corner}</g></svg>`;
    }
    case "brand_plate": {
      const g = Array.isArray(p.gradient) ? p.gradient : [];
      const [gx, gy] = Array.isArray(p.center) ? p.center.map((v) => num(v)) : [W * 0.32, H * 0.45];
      const R = p.rings || {};
      const [rcx, rcy] = Array.isArray(R.center) ? R.center.map((v) => num(v)) : [gx, gy];
      const rings = (R.radii || [])
        .map(
          (r, i) =>
            `<circle cx="${rcx}" cy="${rcy}" r="${num(r)}" fill="none" stroke="#fff" stroke-opacity="${Math.max(0.08, 0.26 - i * 0.025)}" stroke-width="${i === 2 ? 2.4 : 1.6}"/>`
        )
        .join("");
      const dashed = R.dashed_r
        ? `<circle cx="${rcx}" cy="${rcy}" r="${num(R.dashed_r)}" fill="none" stroke="#fff" stroke-opacity=".35" stroke-width="2" stroke-dasharray="3 14"/>`
        : "";
      const arcs = (R.arcs || [])
        .map((a) => {
          const L = (2 * Math.PI * num(a.r) * num(a.len_deg ?? a.len)) / 360;
          return `<circle cx="${rcx}" cy="${rcy}" r="${num(a.r)}" fill="none" stroke="${col(a.color, "#FFFFFF")}" stroke-width="4.5" stroke-linecap="round" stroke-dasharray="${L.toFixed(0)} 99999" transform="rotate(${num(a.from_deg)} ${rcx} ${rcy})"/>`;
        })
        .join("");
      let dots = "";
      let seed = 7;
      const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
      for (let i = 0; i < Math.min(num(p.particles), 200); i++) {
        dots += `<circle cx="${(rnd() * W).toFixed(0)}" cy="${(rnd() * H).toFixed(0)}" r="${(1 + rnd() * 2.4).toFixed(1)}" fill="#fff" opacity="${(0.12 + rnd() * 0.25).toFixed(2)}"/>`;
      }
      return `<svg ${NS} viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice"><defs><radialGradient id="g" cx="${((gx / W) * 100).toFixed(1)}%" cy="${((gy / H) * 100).toFixed(1)}%" r="75%"><stop offset="0" stop-color="${col(g[0], "#2A5BC6")}"/><stop offset=".45" stop-color="${col(g[1], "#183690")}"/><stop offset="1" stop-color="${col(g[2], "#0C225D")}"/></radialGradient></defs><rect width="${W}" height="${H}" fill="url(#g)"/>${rings}${dashed}${arcs}${dots}</svg>`;
    }
    // No "custom" (raw markup) kind: dropped by the security review 2026-10-04;
    // the mw rejects it and planToBoxSpecs skips anything that lands here.
    default:
      throw new Error("Unknown svg kind: " + kind);
  }
}

export const renderSvg = (el) => uri(svgMarkup(el));

/* ---------- text → editor HTML (BasicText state.__text) ---------- */
function textFamily(el, plan) {
  const t = el.text;
  const d = (plan && plan.design) || {};
  const requested =
    t.font === "display" ? d.display_font : t.font === "body" ? d.body_font : t.font;
  return isSafeFontName(requested) ? requested : "Geist";
}
const fontStack = (fam) => SERIF_STACK[fam] || `'${fam}',sans-serif`;
const runText = (el) => (el.text.runs || []).map((r) => r.text).join("");
// A "\n" inside a run is a pinned line break (mw plan-boxes.ts runHtml): lifted text keeps the picture's lines.
const runHtml = (s) => escText(s).replace(/\n/g, "<br>");

// Extract-stage (lifted image text) element ids: x<image>-… (mw extract/build.ts).
const LIFTED_ID_RE = /^x\d+-/;
// Perceived brightness of a #rgb / #rrggbb / rgb() colour, 0..1 (unknown → 1: treat as light).
function brightness(color) {
  const c = String(color);
  let ch;
  if (c[0] === "#") {
    const h = c.length === 4 ? c[1] + c[1] + c[2] + c[2] + c[3] + c[3] : c.slice(1, 7);
    ch = [0, 2, 4].map((k) => parseInt(h.slice(k, k + 2), 16));
  } else {
    ch = (c.match(/[0-9.]+/g) || []).slice(0, 3).map(Number);
  }
  if (ch.length < 3 || ch.some((v) => !Number.isFinite(v))) return 1;
  return (0.299 * ch[0] + 0.587 * ch[1] + 0.114 * ch[2]) / 255;
}
// Lifted text reproduces the picture's own text and pills carry their own background: no
// shadow. Designed text: a soft one only when it is light (it may sit on a photo); a dark
// shadow behind dark text reads as a smudge.
function textShadow(el, pill, color) {
  if (pill || LIFTED_ID_RE.test(String(el.id))) return "none";
  return brightness(color) > 0.6 ? "0 1px 4px rgba(0,0,0,.35)" : "none";
}

export function textHtml(el, plan) {
  const t = el.text;
  const stack = fontStack(textFamily(el, plan));
  const runs = (t.runs || [])
    .map((r) => {
      const st = [];
      if (r.color && COLOR_RE.test(r.color)) st.push(`color:${r.color}`);
      if (r.italic) st.push("font-style:italic");
      if (r.strike && COLOR_RE.test(r.strike)) {
        st.push(
          `text-decoration:line-through;text-decoration-color:${r.strike};text-decoration-thickness:3px`
        );
      }
      return st.length
        ? `<span style="${st.join(";")}">${runHtml(r.text)}</span>`
        : runHtml(r.text);
    })
    .join("");
  const pill = !!t.container;
  const size = num(t.size_px, 40);
  const color = safeColor(t.color, "#FFFFFF");
  return `<p style="margin:0;${pill ? `padding:${PILL_VPAD}em ${PILL_HPAD}em;` : ""}line-height:1.12;font-family:${stack};font-weight:${num(t.weight, 500)};font-style:${t.italic ? "italic" : "normal"};font-size:${(size / PX_PER_EM).toFixed(3)}em;color:${color};letter-spacing:${num(t.letter_spacing_em)}em;text-align:${t.align === "center" ? "center" : "left"};white-space:${t.wrap ? "normal" : "nowrap"};text-shadow:${textShadow(el, pill, color)}">${runs}</p>`;
}

export const PILL_HPAD = 0.6;
export const PILL_VPAD = 0.3;
export const PILL_MAX_RADIUS_PX = 28;

/**
 * Pill (container) fit: the size shrinks until text + padding fit the width, the box height becomes
 * lines × 1.12 × size + vertical padding (vertical centre kept). `line` = slacked one-line width at size_px.
 */
export function fitPill(el, line) {
  const t = el.text;
  const size = num(t.size_px, 40);
  const w = num(el.w);
  const need = line + 2 * PILL_HPAD * size;
  const s = !line || need <= w ? size : Math.max(24, Math.floor((size * w) / need));
  const lines = t.wrap
    ? Math.max(1, Math.ceil((line * s) / size / Math.max(1, w - 2 * PILL_HPAD * s)))
    : 1;
  const h = Math.round(s * (1.12 * lines + 2 * PILL_VPAD));
  const y = el.h == null ? num(el.y) : Math.round(num(el.y) + (num(el.h) - h) / 2);
  return { ...el, y, h, text: { ...t, size_px: s } };
}

/**
 * Makes a text element fit its box (plan px). `measure(el)` returns the natural
 * one-line width at `el.text.size_px`, or null when it cannot measure (→ as is).
 * Plain text: widen up to the safe edge (left edge kept; centred text grows
 * around its centre), then shrink the size, and below 70% keep 70% and wrap.
 * Pills (container): fitPill. `keepW`: never widen past the box (a resize), shrink instead.
 */
export function fitTextToBox(el, measure, keepW = false) {
  const t = el.text;
  if (t.preMeasured) {
    if (!LIFTED_ID_RE.test(String(el.id))) return el; // mw already measured it (plan-boxes.ts)
    // lifted image text: OCR box + slack, but the editor font is not the picture's — a line that
    // runs past it shrinks in place (never widens or moves off the picture)
    keepW = true;
  }
  const natural = measure(el);
  const w = num(el.w);
  if (t.container) return fitPill(el, (natural || 0) * FIT_SLACK);
  if (!natural || natural * FIT_SLACK <= w) return el;
  const need = natural * FIT_SLACK;
  const size = num(t.size_px, 40);
  const x = num(el.x);
  const centre = t.align === "center";
  const cx = x + w / 2;
  const maxW = keepW
    ? w
    : Math.max(w, centre ? 2 * Math.min(cx - SAFE_LEFT, SAFE_RIGHT - cx) : SAFE_RIGHT - x);
  const fitted = (newW, text) => ({
    ...el,
    x: centre ? Math.round(cx - newW / 2) : x,
    w: newW,
    ...(text.wrap ? { h: null } : {}),
    text,
  });
  if (need <= maxW) return fitted(Math.ceil(need), t);
  const scale = maxW / need;
  return scale >= MIN_SIZE_SCALE
    ? fitted(maxW, { ...t, size_px: Math.floor(size * scale) })
    : fitted(maxW, { ...t, size_px: Math.round(size * MIN_SIZE_SCALE), wrap: true });
}

/**
 * Browser-only: loads the plan's text fonts (Google ones are linked first, the
 * same css2 link useEditorFonts would add) and returns a canvas-2D `measure`
 * for fitTextToBox, or null when there is no canvas (jsdom/SSR).
 */
export async function planTextMeasure(plan) {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return null;
  const texts = ((plan && plan.elements) || []).filter((el) => el && el.type === "text");
  const font = (el, size = num(el.text.size_px, 40)) =>
    `${el.text.italic ? "italic " : ""}${num(el.text.weight, 500)} ${size}px ${fontStack(textFamily(el, plan))}`;
  const weights = new Map();
  texts.forEach((el) => {
    const fam = textFamily(el, plan);
    if (!isSupportedGoogleFont(fam)) return;
    if (!weights.has(fam)) weights.set(fam, new Set());
    weights.get(fam).add(num(el.text.weight, 500));
  });
  const href = (generateGoogleFontsLink(weights).match(/href="([^"]+)"/) || [])[1];
  if (href && ![...document.querySelectorAll("link")].some((l) => l.href === href)) {
    const link = Object.assign(document.createElement("link"), { rel: "stylesheet", href });
    document.head.appendChild(link);
    await new Promise((resolve) => {
      link.onload = link.onerror = resolve;
    });
  }
  // ponytail: 3 s cap — a font that never loads is measured with its fallback.
  await Promise.race([
    Promise.all(texts.map((el) => document.fonts.load(font(el)).catch(() => null))),
    new Promise((resolve) => setTimeout(resolve, 3000)),
  ]);
  return (el) => {
    ctx.font = font(el);
    // the widest of its pinned lines
    return Math.max(
      ...runText(el)
        .split("\n")
        .map(
          (line) =>
            ctx.measureText(line).width +
            num(el.text.letter_spacing_em) * num(el.text.size_px, 40) * line.length
        )
    );
  };
}

// Pill container → the BasicText box's own style (same keys the reference used).
function textBoxStyle(el) {
  const c = el.text.container;
  if (!c) return { padding: 0 };
  const bg = safeColor(c.bg, "rgba(8,10,20,0.55)");
  return {
    padding: 0,
    backgroundColor: bg,
    borderWidth: num(c.border_width, 1),
    borderStyle: "solid",
    borderColor: safeColor(c.border_color, bg),
    // A bare number would render as value/7 em; a "px" string passes through untouched.
    borderRadius: `${Math.min(num(c.radius, PILL_MAX_RADIUS_PX), num(el.h, 2 * PILL_MAX_RADIUS_PX) / 2)}px`,
  };
}

function findPreset(list, name) {
  if (!name) return null;
  const resolved = normalizeAnimationName(name);
  return list.find((p) => p.name === resolved) || null;
}

/**
 * One spec per text/svg/image element, in plan order (= layer order). `enter`/`exit`
 * carry the FULL preset object (what boxAnimation.animation stores) or are null
 * for an unknown name. Ids embed "-mdir-" so a re-run can find and replace them.
 * An svg element whose kind svgMarkup does not know, or an image
 * without an https src, is skipped (no box).
 */
export function planToBoxSpecs(plan, { navId, measure }) {
  const stamp = Date.now();
  const enterPresets = getBoxAnimationPresets();
  const exitPresets = getBoxExitAnimationPresets();
  return ((plan && plan.elements) || [])
    .filter((el) => el && (el.type === "text" || el.type === "svg" || el.type === "image"))
    .map((source, n) => {
      const el =
        source.type === "text" && measure ? fitTextToBox(source, (e) => measure(e, plan)) : source;
      const slug = String(el.id || "el")
        .replace(/[^a-z0-9-]/gi, "")
        .slice(0, 40);
      const id = `${ID_PREFIX_BOX}${stamp}-${MDIR_ID_TAG}-${slug}-${n}`;
      const isText = el.type === "text";
      const ids = {
        parent: navId,
        container: 0,
        page: navId,
        id,
        position: { x: pct(el.x, CANVAS_W) + "%", y: pct(el.y, CANVAS_H) + "%", type: "absolute" },
      };
      if (isText) ids.initialState = { __text: textHtml(el, plan) };
      else if (el.type === "image") {
        const src = el.image && el.image.src;
        if (!isAllowedAssetUrl(src)) return null;
        ids.url = src;
      } else {
        try {
          ids.url = renderSvg(el);
        } catch {
          return null; // unknown svg kind → skip this element
        }
      }
      const enter = el.enter && findPreset(enterPresets, el.enter.name);
      const exit = el.exit && findPreset(exitPresets, el.exit.name);
      return {
        id,
        plugin: isText ? "BasicText" : "HotspotImages",
        ids,
        style: isText ? textBoxStyle(el) : {},
        structure: {
          width: pct(el.w, CANVAS_W),
          height: el.h == null ? "auto" : pct(el.h, CANVAS_H),
          widthUnit: "%",
          heightUnit: "%",
          aspectRatio: false,
        },
        timeline: {
          startFrame: Math.round(num(el.start_s) * FPS),
          endFrame: Math.round(num(el.end_s) * FPS),
        },
        enter: enter ? { preset: enter, duration: num(el.enter.dur_s, 0.6) } : null,
        exit: exit ? { preset: exit, duration: num(el.exit.dur_s, 0.5) } : null,
      };
    })
    .filter(Boolean);
}
